'use client';
import React, { useEffect, useState } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import InputString from '@/components/ui/InputString';
import RangeSlider from '@/components/ui/rangeSlider';
import { useOptions } from '@/lib/api/common/options.queries';
import { useActiveProfessions } from '@/features/jobs/api/jobs.queries';
import { featuresOptions } from '@/features/jobs/components/sidebar/FeaturedCategoryComponent';
import { POST_DATE_OPTIONS } from '@/features/jobs/components/sidebar/PostDateComponent';
import {
  generateLink,
  getOptionLabelByLang,
  SidebarOption,
} from '@/features/jobs/components/sidebar/sidebar.utils';
import CollapsibleSection from './CollapsibleSection';
import { JobListFilterDto } from '@/shared/job/job.public.dto';

type SalaryRange = [number, number];

// Stable fallback so effect deps don't change on every render while queries load.
const EMPTY_OPTIONS: SidebarOption[] = [];

/** Form state for advanced filter (multi-select fields stay as string[] until submit). */
type AdvancedFilterForm = Omit<
  JobListFilterDto,
  'professionIds' | 'locationIds' | 'timeType' | 'level' | 'branchId' | 'dayAgo'
> & {
  professionIds: string[];
  locationIds: string[];
  timeType: string[];
  level: string[];
  branchId: string;
  dayAgo: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
  salaryMin?: number;
  salaryMax?: number;
};

const asStringArray = (
  value: string | string[] | number | undefined | null
): string[] => {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) return value.map(String);
  return String(value)
    .split(',')
    .filter(Boolean);
};

const splitArrayIntoThreeParts = (array: SidebarOption[]) => {
  const partSize = Math.ceil(array.length / 3);
  return [
    array.slice(0, partSize),
    array.slice(partSize, partSize * 2),
    array.slice(partSize * 2),
  ];
};

const findOptionById = (
  selectedIds: string[],
  options: SidebarOption[],
  type: string
): SidebarOption[] => {
  return selectedIds
    .map((id) => {
      if (type === 'category') {
        const mainOption = options.find((opt) => String(opt.id) === id);
        if (mainOption) return mainOption;
        for (const option of options) {
          const nested = option.professions ?? option.children ?? [];
          const childOption = nested.find((child) => String(child.id) === id);
          if (childOption) return childOption;
        }
        return undefined;
      }
      if (type === 'location') {
        for (const option of options) {
          const match = [option, ...(option.children ?? [])].find(
            (child) => String(child.id) === id
          );
          if (match) return match;
        }
        return undefined;
      }
      return options.find((opt) => String(opt.id) === id);
    })
    .filter((option): option is SidebarOption => option !== undefined);
};

const AdvancedFilterComponent = ({
  isOpen,
  onClose,
  categoryId,
  skillId,
  workTypeId,
  locationId,
  salaryMin,
  salaryMax,
  isEnglish = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  categoryId?: number;
  skillId?: number;
  workTypeId?: number;
  locationId?: number;
  salaryMin?: number;
  salaryMax?: number;
  isEnglish?: boolean;
}) => {
  const searchParams = useSearchParams();
  const branchIdParam = searchParams.get('branchId');
  const router = useRouter();
  const { data: workTypeOptions = EMPTY_OPTIONS } = useOptions({ gkey: 'worktime' });
  const { data: skillLevelOptions = EMPTY_OPTIONS } = useOptions({
    gkey: 'position_level',
  });
  const { data: locationOptions = EMPTY_OPTIONS } = useOptions({
    gkey: 'location',
    withChildren: true,
  });
  const { data: businessCategoryOptions = EMPTY_OPTIONS } = useOptions({
    gkey: 'branch',
  });

  const [selectedCategory, setSelectedCategory] =
    useState<SidebarOption | null>(null);
  const { data: professions = EMPTY_OPTIONS } = useActiveProfessions(
    Number(selectedCategory?.id) || 0
  );

  const { register, handleSubmit, reset, watch, setValue } =
    useForm<AdvancedFilterForm>({
      defaultValues: {
        type: 'all',
        page: 1,
        limit: 10,
        sortBy: 'created_at',
        sortOrder: 'desc',
        search: '',
        professionIds: [],
        locationIds: [],
        timeType: [],
        level: [],
        salaryMin: 0,
        salaryMax: 20000000,
        dayAgo: '',
        branchId: '',
      },
    });

  const [salaryRange, setSalaryRange] = useState<SalaryRange>([0, 20000000]);
  const [inputValues, setInputValues] = useState({
    min: '0',
    max: '20000000',
  });

  const [firstColumn, secondColumn, thirdColumn] = splitArrayIntoThreeParts(
    businessCategoryOptions
  );
  const [childFirstColumn, childSecondColumn, childThirdColumn] =
    splitArrayIntoThreeParts(professions);

  const formatSalary = (value: number): string => {
    return new Intl.NumberFormat('mn-MN').format(value) + '₮';
  };

  useEffect(() => {
    setValue('branchId', selectedCategory ? String(selectedCategory.id) : '');
  }, [selectedCategory, setValue]);

  useEffect(() => {
    if (!categoryId) {
      setSelectedCategory(null);
      return;
    }
    const category = businessCategoryOptions.find(
      (cat) => cat.id === categoryId
    );
    setSelectedCategory(category ?? null);
  }, [businessCategoryOptions, categoryId]);

  useEffect(() => {
    reset({ level: skillId ? [skillId.toString()] : [] });
  }, [skillId, reset]);

  useEffect(() => {
    reset({ timeType: workTypeId ? [workTypeId.toString()] : [] });
  }, [workTypeId, reset]);

  useEffect(() => {
    reset({ locationIds: locationId ? [locationId.toString()] : [] });
  }, [locationId, reset]);

  useEffect(() => {
    setSalaryRange([salaryMin || 0, salaryMax || 20000000]);
    setInputValues(
      salaryMin && salaryMax
        ? { min: salaryMin.toString(), max: salaryMax.toString() }
        : { min: '0', max: '20000000' }
    );
  }, [salaryMin, salaryMax]);

  useEffect(() => {
    reset({
      timeType: searchParams.get('timeType')?.split(',') || [],
      level: searchParams.get('level')?.split(',') || [],
      locationIds: searchParams.get('locationIds')?.split(',') || [],
      professionIds: searchParams.get('professionIds')?.split(',') || [],
      dayAgo: searchParams.get('dayAgo') || '',
      branchId: branchIdParam || '',
    });
    if (searchParams.get('salaryMin') && searchParams.get('salaryMax')) {
      setSalaryRange([
        Number(searchParams.get('salaryMin')),
        Number(searchParams.get('salaryMax')),
      ]);
      setInputValues({
        min: searchParams.get('salaryMin') || '0',
        max: searchParams.get('salaryMax') || '20000000',
      });
    }
  }, [searchParams, branchIdParam, reset]);

  const onSubmit: SubmitHandler<AdvancedFilterForm> = (payload) => {
    const params = new URLSearchParams(searchParams.toString());
    (Object.keys(payload) as (keyof AdvancedFilterForm)[]).forEach((key) => {
      const value = payload[key];
      const paramKey = String(key);
      if (value && String(value).length > 0) {
        params.set(paramKey, String(value));
      } else {
        params.delete(paramKey);
      }
    });
    if (salaryRange[0] !== 0 || salaryRange[1] !== 20000000) {
      params.set('salaryMin', salaryRange[0].toString());
      params.set('salaryMax', salaryRange[1].toString());
    }
    params.set('page', '1');
    router.push(`/job/list?${String(params)}`);
    onClose();
  };

  const handleSliderChange = (value: SalaryRange) => {
    setSalaryRange(value);
    setInputValues({
      min: value[0].toString(),
      max: value[1].toString(),
    });
  };

  const handleInputChange = (type: 'min' | 'max', value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    setInputValues((prev) => ({
      ...prev,
      [type]: numericValue,
    }));
  };

  const handleInputBlur = () => {
    const minValue = Math.min(Number(inputValues.min), Number(inputValues.max));
    const maxValue = Math.max(Number(inputValues.min), Number(inputValues.max));
    const validMinValue = Math.max(0, Math.min(minValue, 20000000));
    const validMaxValue = Math.max(0, Math.min(maxValue, 20000000));
    setSalaryRange([validMinValue, validMaxValue]);
    setInputValues({
      min: validMinValue.toString(),
      max: validMaxValue.toString(),
    });
  };

  const formValues = watch();

  const handleRemove = (id: number | string, type: string) => {
    const asString = String(id);
    if (type === 'location') {
      setValue(
        'locationIds',
        asStringArray(formValues.locationIds).filter((item) => item !== asString)
      );
    }
    if (type === 'workType') {
      setValue(
        'timeType',
        asStringArray(formValues.timeType).filter((item) => item !== asString)
      );
    }
    if (type === 'skillLevel') {
      setValue(
        'level',
        asStringArray(formValues.level).filter((item) => item !== asString)
      );
    }
    if (type === 'category') {
      setValue(
        'professionIds',
        asStringArray(formValues.professionIds).filter(
          (item) => item !== asString
        )
      );
    }
    if (type === 'postDate') {
      setValue('dayAgo', '');
    }
    if (type === 'mainCategory') {
      setSelectedCategory(null);
      setValue('professionIds', []);
      setValue('branchId', '');
    }
  };

  const handleClearFilter = () => {
    setSelectedCategory(null);
    reset({
      timeType: [],
      level: [],
      locationIds: [],
      professionIds: [],
      branchId: '',
      dayAgo: '',
    });
  };

  const FilterTags = ({
    groups,
  }: {
    groups: {
      label: string;
      values: string[];
      availableOptions: SidebarOption[];
      type: string;
    }[];
  }) => (
    <div className="flex flex-wrap gap-2">
      {groups.map(({ label, values, availableOptions, type }) => {
        const selectedOptions = findOptionById(values, availableOptions, type);
        return selectedOptions.map((option, index) => (
          <div key={`${label}-${index}`} className="flex flex-col">
            {index === 0 && <div className="text-primary">{label}</div>}
            <div className="px-3 py-2 rounded-md bg-primary text-white flex gap-2 items-center mt-auto">
              {getOptionLabelByLang(option, isEnglish)}
              <X
                size="16"
                onClick={() => handleRemove(option.id, type)}
                className="cursor-pointer hover:text-red-500 transition-colors"
              />
            </div>
          </div>
        ));
      })}
    </div>
  );

  const modalFooter = (
    <div>
      <div className="flex flex-wrap gap-4 p-4 max-h-48 overflow-y-auto">
        <div className="text-sm">
          <FilterTags
            groups={[
              {
                label: isEnglish ? 'Industry & Occupation' : 'Салбар мэргэжил',
                values: selectedCategory ? [String(selectedCategory.id)] : [],
                availableOptions: businessCategoryOptions,
                type: 'mainCategory',
              },
              {
                label: isEnglish ? 'Industry' : 'Салбар',
                values: formValues.professionIds || [],
                availableOptions: [
                  ...businessCategoryOptions,
                  ...professions,
                ],
                type: 'category',
              },
              {
                label: isEnglish ? 'Job type' : 'Ажиллах цаг',
                values: formValues.timeType || [],
                availableOptions: workTypeOptions,
                type: 'workType',
              },
              {
                label: isEnglish ? 'Job Level' : 'Мэргэжлийн түвшин',
                values: formValues.level || [],
                availableOptions: skillLevelOptions,
                type: 'skillLevel',
              },
              {
                label: isEnglish ? 'Location' : 'Байршил',
                values: formValues.locationIds || [],
                availableOptions: locationOptions,
                type: 'location',
              },
              {
                label: isEnglish ? 'Published' : 'Нийтлэгдсэн',
                values: formValues.dayAgo ? [String(formValues.dayAgo)] : [],
                availableOptions: POST_DATE_OPTIONS,
                type: 'postDate',
              },
            ]}
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 py-4 px-4 sm:px-6">
        <Button
          className="rounded-full font-bold h-9 px-6 border-transparent"
          variant="outline"
          onClick={handleClearFilter}
        >
          {isEnglish ? 'Clear' : 'Цэвэрлэх'}
        </Button>
        <Button
          className="rounded-full font-bold h-9 px-6"
          variant="primary"
          form="filter-form"
          type="button"
          onClick={handleSubmit(onSubmit)}
        >
          {isEnglish ? 'Search' : 'Хайх'}
        </Button>
      </div>
    </div>
  );

  return (
    <div>
      <Modal
        isOpen={isOpen}
        setIsOpen={onClose}
        title={isEnglish ? 'Advanced filter' : 'Дэлгэрэнгүй шүүлтүүр'}
        size="medium"
        footer={modalFooter}
      >
        <div className="px-4">
          <form className="flex flex-col gap-4" id="filter-form">
            <CollapsibleSection header={'Онцлох'}>
              <div className="grid grid:cols-1 lg:grid-cols-3 gap-4 text-sm py-4">
                {featuresOptions.map((item) => (
                  <Link
                    href={generateLink('features', item.id, searchParams)}
                    key={`feature-${item.id}`}
                    className="flex items-center gap-1"
                    onClick={() => onClose()}
                  >
                    <ChevronRight size="16" className="text-primary shrink-0" />
                    <span>{item.name}</span>
                  </Link>
                ))}
              </div>
            </CollapsibleSection>
            <CollapsibleSection
              header={
                <>
                  {selectedCategory ? (
                    <span
                      className="pl-2 flex items-center gap-1 cursor-pointer"
                      onClick={() => setSelectedCategory(null)}
                    >
                      {isEnglish ? 'Industry & Occupation' : 'Салбар мэргэжил'}
                      <ChevronRight size="16" />
                    </span>
                  ) : (
                    <span className="pl-2 flex items-center gap-1">
                      {isEnglish ? 'Industry & Occupation' : 'Салбар мэргэжил'}
                    </span>
                  )}
                  {selectedCategory && (
                    <span className="pl-2 flex items-center gap-1">
                      {getOptionLabelByLang(selectedCategory, isEnglish)}
                    </span>
                  )}
                </>
              }
            >
              <div className="grid grid:cols-1 lg:grid-cols-3 gap-4 text-sm py-4">
                {(selectedCategory
                  ? [childFirstColumn, childSecondColumn, childThirdColumn]
                  : [firstColumn, secondColumn, thirdColumn]
                ).map((items, columnIndex) => (
                  <div key={columnIndex}>
                    <ul className="space-y-4">
                      {items.map((item) =>
                        selectedCategory ? (
                          <li key={item.id} className="flex items-center gap-1">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                {...register('professionIds')}
                                value={item.id}
                              />
                              <span>
                                {getOptionLabelByLang(item, isEnglish)}
                              </span>
                            </label>
                          </li>
                        ) : (
                          <li
                            key={item.id}
                            className="flex items-center gap-1 cursor-pointer shrink-0"
                            onClick={() => setSelectedCategory(item)}
                          >
                            <ChevronRight
                              size="16"
                              className="text-primary"
                            />
                            {getOptionLabelByLang(item, isEnglish)}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
            <CollapsibleSection
              header={isEnglish ? 'Job type' : 'Ажиллах цагийн төрөл'}
            >
              <div className="pt-4">
                <ul className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
                  {workTypeOptions.map((item) => (
                    <li key={`work-${item.id}`}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          {...register('timeType')}
                          value={item.id}
                        />
                        <span>{getOptionLabelByLang(item, isEnglish)}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            </CollapsibleSection>
            <CollapsibleSection
              header={isEnglish ? 'Job Level' : 'Мэргэжлийн түвшин'}
            >
              <div className="pt-4">
                <ul className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
                  {skillLevelOptions.map((item) => (
                    <li key={`skill-${item.id}`}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          {...register('level')}
                          value={item.id}
                        />
                        <span>{getOptionLabelByLang(item, isEnglish)}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            </CollapsibleSection>
            <CollapsibleSection header={isEnglish ? 'Salary' : 'Цалин'}>
              <div className="pt-4">
                <div className="px-4 py-2 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="flex items-center gap-4 mb-4">
                    <InputString
                      value={inputValues.min}
                      onChange={(e) => handleInputChange('min', e.target.value)}
                      onBlur={handleInputBlur}
                      placeholder="Доод хэмжээ"
                      className="w-32"
                    />
                    <span>-</span>
                    <InputString
                      value={inputValues.max}
                      onChange={(e) => handleInputChange('max', e.target.value)}
                      onBlur={handleInputBlur}
                      placeholder="Дээд хэмжээ"
                      className="w-32"
                    />
                  </div>
                  <div className="w-full">
                    <div className="mb-4 mt-4 w-full">
                      <RangeSlider
                        min={0}
                        max={20000000}
                        step={100000}
                        salaryRange={salaryRange}
                        onChange={(value: number | number[]) =>
                          handleSliderChange(value as [number, number])
                        }
                      />
                    </div>
                    <div className="flex justify-between mb-2 text-sm">
                      <span>{formatSalary(salaryRange[0])}</span>
                      <span>
                        {salaryRange[1] === 20000000
                          ? formatSalary(salaryRange[1]) + '-с дээш'
                          : formatSalary(salaryRange[1])}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleSection>
            <CollapsibleSection
              header={isEnglish ? 'Published date' : 'Нийтлэгдсэн огноо'}
            >
              <div className="pt-4">
                <ul className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  {POST_DATE_OPTIONS.map((item) => (
                    <li key={`post-date-${item.id}`}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          {...register('dayAgo')}
                          value={item.id}
                        />
                        <span>{getOptionLabelByLang(item, isEnglish)}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            </CollapsibleSection>
            <CollapsibleSection header={isEnglish ? 'Location' : 'Байршил'}>
              <div className="py-4 border-b">
                <ul className="flex flex-wrap gap-4 text-sm">
                  {locationOptions.map((item) => {
                    if (item.id !== 1) return null;
                    return (
                      <React.Fragment key={`location-${item.id}`}>
                        <li>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('locationIds')}
                              value={item.id}
                            />
                            <span>
                              {getOptionLabelByLang(item, isEnglish)}
                            </span>
                          </label>
                        </li>
                        {item.children?.map((child) => (
                          <li key={`location-${child.id}`}>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                {...register('locationIds')}
                                value={child.id}
                              />
                              <span>
                                {getOptionLabelByLang(child, isEnglish)}
                              </span>
                            </label>
                          </li>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </ul>
              </div>
              <div className="py-4">
                <ul className="grid grid-cols-1 lg:grid-cols-4 gap-4 text-sm">
                  {locationOptions.map((item) => {
                    if (item.id === 1) return null;
                    return (
                      <li key={`location-${item.id}`}>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('locationIds')}
                            value={item.id}
                          />
                          <span>{getOptionLabelByLang(item, isEnglish)}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </CollapsibleSection>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default AdvancedFilterComponent;
